import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { SuperSEO } from 'react-super-seo';
import moment from 'moment';
import * as _ from 'lodash';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

import getSelfDriveCars from '../config/selfDriveCars';
import getWithDriverCars from '../config/withDriverCars';
import getSelfDriveBikes from '../config/selfDriveBikes';
import discounts from '../config/discount';
import * as charges from '../config/charges.json';
import * as pickDropConfig from '../config/city.json';
import { Fleet } from '../models/cars.model';
import { getAvailableKMRanges } from '../utils/rangeRules';
import { buildUpiUri, buildQrCodeUrl } from '../utils/upi';
import { FEATURE_FLAGS } from '../config/featureFlags';

const CustomDatePickerInput = React.forwardRef(({ value, onClick }: any, ref: any) => (
    <div
        className="custom-datetime-picker-input form-control d-flex align-items-center justify-content-between"
        onClick={onClick}
        ref={ref}
        style={{ cursor: 'pointer', height: '46px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px 15px', width: '100%', boxSizing: 'border-box' }}
    >
        <span style={{ color: value ? '#0f172a' : '#64748b', fontSize: '14px', fontWeight: 500 }}>{value || "Select Date & Time"}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ color: 'var(--primary-color)', flexShrink: 0 }}>
            <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1z" />
            <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
        </svg>
    </div>
));

function VehicleDetail() {
    const { serviceType, location, slug } = useParams();
    const [searchParams] = useSearchParams();

    // Verification modal and authentication states
    const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
    const [paymentStep, setPaymentStep] = useState<'verify' | 'upi'>('verify');
    const [transactionId, setTransactionId] = useState<string>('');

    // Registered Users list
    const [registeredUsers, setRegisteredUsers] = useState<any[]>(() => {
        const saved = localStorage.getItem('registeredUsers');
        const list = saved ? JSON.parse(saved) : [];
        const current = localStorage.getItem('currentUser');
        if (current) {
            const currentUserData = JSON.parse(current);
            const exists = list.some((u: any) => u.email.toLowerCase() === currentUserData.email.toLowerCase());
            if (!exists) {
                const seededUser = {
                    name: currentUserData.name,
                    email: currentUserData.email,
                    phone: currentUserData.phone,
                    password: 'password123'
                };
                list.push(seededUser);
                localStorage.setItem('registeredUsers', JSON.stringify(list));
            }
        }
        return list;
    });

    const [userVerification, setUserVerification] = useState<{ name: string; email: string; phone: string } | null>(() => {
        const saved = localStorage.getItem('currentUser');
        return saved ? JSON.parse(saved) : null;
    });
    const [authForm, setAuthForm] = useState({
        name: userVerification?.name || '',
        email: userVerification?.email || '',
        phone: userVerification?.phone || '',
        password: '',
        confirmPassword: ''
    });
    const [authStep, setAuthStep] = useState<'email' | 'login' | 'register'>('email');
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [agreeBookingTerms, setAgreeBookingTerms] = useState(false);
    const [authError, setAuthError] = useState('');
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

    // Dynamically retrieve pickup and dropoff points for the current hub location from config
    const getHubPoints = () => {
        const pickupPoints: string[] = [];
        const dropoffPoints: string[] = [];

        if (!location || !serviceType) return { pickupPoints, dropoffPoints };

        const cityName = _.startCase(location);
        const targetService = _.startCase(serviceType);
        const cityData = (pickDropConfig as any)[cityName];

        if (cityData && cityData.config) {
            cityData.config.forEach((config: any) => {
                if (config.service === targetService) {
                    pickupPoints.push(...config.pickup);
                    dropoffPoints.push(...config.dropoff);
                }
            });
        }

        return { pickupPoints, dropoffPoints };
    };

    const { pickupPoints, dropoffPoints } = getHubPoints();

    // Find the vehicle based on the slug
    const findVehicle = (): (Fleet & { serviceKey: string }) | null => {
        if (!slug) return null;

        const selfDriveCars = getSelfDriveCars().map(c => ({ ...c, serviceKey: 'self-driving-car-rental' }));
        const withDriverCars = getWithDriverCars().map(c => ({ ...c, serviceKey: 'chauffeur-driven-car-rental' }));
        const selfDriveBikes = getSelfDriveBikes().map(b => ({ ...b, serviceKey: 'self-driving-bike-rental' }));

        const all = [...selfDriveCars, ...withDriverCars, ...selfDriveBikes];

        const matched = all.find(v => {
            const vehicleSlug = _.kebabCase(`${v.Brand}-${v.Name}-${v.isSelfDrive ? 'self-drive' : 'with-driver'}`);
            return vehicleSlug === slug;
        });

        return matched || null;
    };

    const vehicle = findVehicle();

    // Default or query-parameterized states
    const [pickupDate, setPickupDate] = useState<Date>(
        searchParams.get('pickupDate') ? new Date(searchParams.get('pickupDate')!) : new Date()
    );
    const [dropoffDate, setDropoffDate] = useState<Date>(
        searchParams.get('dropoffDate') ? new Date(searchParams.get('dropoffDate')!) : new Date(new Date().setDate(new Date().getDate() + 1))
    );
    const [selectedPickup, setSelectedPickup] = useState<string>(
        searchParams.get('pickup') || (pickupPoints.length > 0 ? pickupPoints[0] : '')
    );
    const [selectedDropoff, setSelectedDropoff] = useState<string>(
        searchParams.get('dropoff') || (dropoffPoints.length > 0 ? dropoffPoints[0] : '')
    );
    const [selectedKMs, setSelectedKMs] = useState<string>(
        searchParams.get('kms') || (vehicle && vehicle.KMIncluded ? vehicle.KMIncluded[0] : '')
    );

    const [activeImgIdx, setActiveImgIdx] = useState<number>(0);

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Sync selected pickup & dropoff if hub coordinates change and no search queries exist
    useEffect(() => {
        const { pickupPoints: newPickups, dropoffPoints: newDropoffs } = getHubPoints();
        if (!searchParams.get('pickup') && newPickups.length > 0) {
            setSelectedPickup(newPickups[0]);
        }
        if (!searchParams.get('dropoff') && newDropoffs.length > 0) {
            setSelectedDropoff(newDropoffs[0]);
        }
    }, [location, serviceType]);

    // If vehicle is not found
    if (!vehicle) {
        return (
            <div className="container py-5 text-center" style={{ minHeight: '60vh', marginTop: '100px' }}>
                <h2 className="font-weight-bold text-dark mb-3">Vehicle Listing Not Found</h2>
                <p className="text-muted mb-4">The vehicle link you followed may have expired or is currently unavailable.</p>
                <Link to="/services" className="btn btn-primary px-4 py-2" style={{ borderRadius: '30px' }}>
                    Browse Available Fleet
                </Link>
            </div>
        );
    }

    const imageList = vehicle.Images && vehicle.Images.length > 0 ? vehicle.Images : [vehicle.Image];

    // Helper functions for pricing calculation (copied from carDetails.component.tsx)
    const convertTo24HourFormat = (time: string) => {
        let [hourStr, period] = time.split(' ');
        let hour = parseInt(hourStr);
        if (period === 'AM') {
            if (hour === 12) return 0;
            return hour;
        } else if (period === 'PM') {
            if (hour === 12) return 24;
            return hour + 12;
        }
        return 9; // Default fallback
    };

    const getDays = () => {
        if (vehicle.isSelfDrive) {
            const startHour = convertTo24HourFormat(vehicle.StartTime);
            const msInADay = 24 * 60 * 60 * 1000;

            const getHoursDifference = (date1: Date, date2: Date): number => {
                return Math.abs(date1.getTime() - date2.getTime()) / (1000 * 60 * 60);
            };

            let adjustedStartDate = new Date(pickupDate);
            let startDayFraction = 0;

            if (pickupDate.getHours() < startHour) {
                const nineAMStartDate = new Date(pickupDate);
                nineAMStartDate.setHours(startHour, 0, 0, 0);
                const hoursBefore9AM = getHoursDifference(pickupDate, nineAMStartDate);
                startDayFraction = vehicle.HalfDayCharges.length > 0 && hoursBefore9AM <= 12 ? 0.5 : 1;
                adjustedStartDate.setHours(startHour, 0, 0, 0);
            } else {
                adjustedStartDate.setHours(startHour, 0, 0, 0);
            }

            let adjustedEndDate = new Date(dropoffDate);
            let endDayFraction = 0;

            if (dropoffDate.getHours() > startHour) {
                const nineAMEndDate = new Date(dropoffDate);
                nineAMEndDate.setHours(startHour, 0, 0, 0);
                const hoursAfter9AM = getHoursDifference(nineAMEndDate, dropoffDate);
                endDayFraction = vehicle.HalfDayCharges.length > 0 && hoursAfter9AM <= 12 ? 0.5 : 1;
                adjustedEndDate.setHours(startHour, 0, 0, 0);
            } else {
                adjustedEndDate.setHours(startHour, 0, 0, 0);
            }

            const diffInMs = adjustedEndDate.getTime() - adjustedStartDate.getTime();
            const fullDays = Math.floor(diffInMs / msInADay);
            const totalDays = fullDays + startDayFraction + endDayFraction;
            return totalDays < 1 ? 1 : totalDays;
        } else {
            if (dropoffDate.toLocaleDateString() === pickupDate.toLocaleDateString()) return 1;
            const diffTime = Math.abs(dropoffDate.getTime() - pickupDate.getTime());
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }
    };

    const isBikeRental = serviceType === 'self-driving-bike-rental';
    const isSelfDriveCar = Boolean((serviceType === 'self-driving-car-rental' || (vehicle && vehicle.isSelfDrive)) && !isBikeRental);

    const rangeInfo = vehicle && vehicle.KMIncluded
        ? getAvailableKMRanges(vehicle.KMIncluded, getDays(), isSelfDriveCar)
        : { allowedKMs: [], isRestricted: false, restrictionMessage: '' };

    useEffect(() => {
        if (vehicle && vehicle.KMIncluded) {
            const { allowedKMs, isRestricted } = getAvailableKMRanges(vehicle.KMIncluded, getDays(), isSelfDriveCar);
            if (isRestricted) {
                if (selectedKMs !== '') setSelectedKMs('');
            } else if (allowedKMs.length > 0) {
                if (!selectedKMs || !allowedKMs.includes(selectedKMs)) {
                    setSelectedKMs(allowedKMs[0]);
                }
            }
        }
    }, [pickupDate, dropoffDate, vehicle, isSelfDriveCar, selectedKMs]);

    const calculateCharges = () => {
        const pricingIndex = vehicle.KMIncluded.indexOf(selectedKMs);
        const splitDays = getDays().toString().split('.');
        const days = parseInt(splitDays[0]);
        const index = pricingIndex >= 0 ? pricingIndex : 0;
        const chargesPerPeriod = vehicle.Charges[index] || 0;
        const halfDayCharge = vehicle.HalfDayCharges[index] || 0;
        return days * chargesPerPeriod + (splitDays.length > 1 ? halfDayCharge : 0);
    };

    const getVehicleCharges = () => {
        const pricingIndex = vehicle.KMIncluded ? vehicle.KMIncluded.indexOf(selectedKMs) : -1;
        const index = pricingIndex >= 0 ? pricingIndex : 0;
        return {
            charges: vehicle.Charges[index] || 0,
            kmPerDay: selectedKMs || (vehicle.KMIncluded ? vehicle.KMIncluded[0] : '150'),
            extraKMCharges: vehicle.ExtraKMCharges[index] || 0
        };
    };

    const isOvernightChargeApplicable = () => {
        return !vehicle.isSelfDrive && getDays() > 1;
    };

    const getOvernightCharges = () => {
        if (isOvernightChargeApplicable() && selectedPickup) {
            return (charges as any)[selectedPickup]?.['Overnight Charges'] * (getDays() - 1) || 0;
        }
        return 0;
    };

    const getPickupCharges = () => {
        return selectedPickup ? (charges as any)[selectedPickup]?.['Convenience Fee'] || 0 : 0;
    };

    const getDropoffCharges = () => {
        return selectedDropoff ? (charges as any)[selectedDropoff]?.['Convenience Fee'] || 0 : 0;
    };

    const getTotal = () => {
        const pickUpCharges = getPickupCharges();
        const dropOffCharges = getDropoffCharges();
        const platformCharges = (selectedPickup && selectedDropoff)
            ? ((charges as any)[selectedPickup]?.['Platform Charges'] || 0) + ((charges as any)[selectedDropoff]?.['Platform Charges'] || 0)
            : 0;

        return vehicle.isSelfDrive
            ? getSelfDriveCharges(pickUpCharges, dropOffCharges, platformCharges)
            : {
                actualCharges: calculateCharges() + platformCharges + getOvernightCharges(),
                total: calculateCharges() + platformCharges + getOvernightCharges()
            };
    };

    const getSelfDriveCharges = (pickUpCharges: number, dropOffCharges: number, platformCharges: number) => {
        const isDiscountApplicable = vehicle.isDiscountApplicable;
        const days = getDays();
        let discount = { "DiscountPercent": 0, "PickDropDiscountPercent": 0 };

        if (days <= 1) discount = discounts["SingleDay"];
        else if (days <= 3) discount = discounts["UptoThreeDays"];
        else if (days <= 6) discount = discounts["UptoSixDays"];
        else if (days > 6) discount = discounts["MoreThanSixDays"];

        const actualCharges = calculateCharges() + pickUpCharges + dropOffCharges + platformCharges;
        const chargesVal = isDiscountApplicable ? calculateCharges() - (Math.round((calculateCharges() * discount.DiscountPercent) / 100)) : calculateCharges();
        const pickUpDiscount = Math.round((pickUpCharges * discount.PickDropDiscountPercent) / 100);
        const dropOffDiscount = Math.round((dropOffCharges * discount.PickDropDiscountPercent) / 100);

        const finalPickup = isDiscountApplicable ? pickUpCharges - pickUpDiscount : pickUpCharges;
        const finalDropoff = isDiscountApplicable ? dropOffCharges - dropOffDiscount : dropOffCharges;

        const total = chargesVal + finalPickup + finalDropoff + platformCharges;
        return { total, actualCharges };
    };

    // Build the dynamic standard UPI URL
    const getUpiUri = () => {
        const amount = getTotal().total;
        return buildUpiUri(amount, `${vehicle.Brand} ${vehicle.Name} Booking`);
    };

    const getMessage = () => {
        if (pickupDate && dropoffDate && selectedPickup && selectedDropoff && userVerification) {
            const { total, actualCharges } = getTotal();
            const discountAmount = actualCharges - total;

            let message = `Hello AapliGaadi,
I have initiated a booking request.

Customer Details:
Name: ${userVerification.name}
Email: ${userVerification.email}
Phone: ${userVerification.phone}

Booking details:
Vehicle: ${vehicle.Brand} ${vehicle.Name}
Start Date & Location: ${moment(pickupDate).format('LLLL')} (${selectedPickup})
End Date & Location: ${moment(dropoffDate).format('LLLL')} (${selectedDropoff})

Total Days: ${getDays()}
Total KM(s) included: ${getVehicleCharges().kmPerDay === 'Unlimited' ? 'Unlimited' : parseInt(getVehicleCharges().kmPerDay) * getDays()} KMs
Fleet Charges: ₹${calculateCharges()}/-
Pickup Charges: ₹${getPickupCharges()}/-
Dropoff Charges: ₹${getDropoffCharges()}/-
Platform Charges: ₹${((charges as any)[selectedPickup]?.['Platform Charges'] || 0) + ((charges as any)[selectedDropoff]?.['Platform Charges'] || 0)}/-
`;

            if (discountAmount > 0) {
                message += `Original Amount: ₹${actualCharges}/-\n`;
                message += `Discount Applied: -₹${discountAmount}/-\n`;
            }

            message += `Total Payable Amount: ₹${total}/-

I have initiated a UPI transaction of ₹${total} to crewtechsolutions@upi.
UPI Transaction ID / Ref No: ${transactionId}

Please verify and confirm my booking.
`;

            if (vehicle.isSelfDrive) {
                message += `\nPlease Note:\n`;
                message += `- Additional Refundable Security Deposit of ₹${vehicle.SecurityDeposit} will be charged.\n`;
                message += `- Additional KM Charges: ₹${getVehicleCharges().extraKMCharges}/- per KM\n`;
                if (discountAmount > 0) {
                    message += `- Special Discount Savings: ₹${discountAmount}/-\n`;
                }
            }

            return encodeURIComponent(message);
        }
        return '';
    };

    const onPickupDateChange = (date: Date | null) => {
        if (!date) return;
        setPickupDate(date);
        const nextDate = new Date(date);
        setDropoffDate(new Date(new Date(nextDate.setDate(nextDate.getDate() + 1)).setTime(date.getTime() + 24 * 60 * 60 * 1000)));
    };

    const getMinDropoffDate = () => {
        const nextDate = new Date(new Date(pickupDate).setTime(pickupDate.getTime() + 24 * 60 * 60 * 1000));
        return new Date(nextDate.setDate(nextDate.getDate()));
    };

    const handleBookingClick = () => {
        const { total, actualCharges } = getTotal();
        const discountAmount = actualCharges - total;

        if (!FEATURE_FLAGS.ENABLE_EXTRA_FEATURES) {
            let message = `Hello AapliGaadi,\nI am interested in booking:\n\n`;
            message += `Vehicle: ${vehicle.Brand} ${vehicle.Name}\n`;
            message += `Start Date & Location: ${moment(pickupDate).format('LLLL')} (${selectedPickup})\n`;
            message += `End Date & Location: ${moment(dropoffDate).format('LLLL')} (${selectedDropoff})\n\n`;
            message += `Total Days: ${getDays()}\n`;
            message += `Total KM(s) included: ${getVehicleCharges().kmPerDay === 'Unlimited' ? 'Unlimited' : parseInt(getVehicleCharges().kmPerDay) * getDays()} KMs\n`;
            message += `Fleet Charges: ₹${calculateCharges()}/-\n`;
            message += `Pickup Charges: ₹${getPickupCharges()}/-\n`;
            message += `Dropoff Charges: ₹${getDropoffCharges()}/-\n`;
            message += `Platform Charges: ₹${((charges as any)[selectedPickup]?.['Platform Charges'] || 0) + ((charges as any)[selectedDropoff]?.['Platform Charges'] || 0)}/-\n`;
            if (discountAmount > 0) {
                message += `Original Amount: ₹${actualCharges}/-\n`;
                message += `Discount Applied: -₹${discountAmount}/-\n`;
            }
            message += `Total Payable Amount: ₹${total}/-\n`;
            if (vehicle.isSelfDrive) {
                message += `\nPlease Note:\n`;
                message += `- Additional Refundable Security Deposit of ₹${vehicle.SecurityDeposit} will be charged.\n`;
                message += `- Additional KM Charges: ₹${getVehicleCharges().extraKMCharges}/- per KM\n`;
                if (discountAmount > 0) {
                    message += `- Special Discount Savings: ₹${discountAmount}/-\n`;
                }
            }
            const whatsappUrl = `https://wa.me/917208388380?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');
            return;
        }

        if (!userVerification) {
            setPaymentStep('verify');
        } else {
            setPaymentStep('upi');
        }
        setShowAuthModal(true);
    };

    const handleEmailContinue = (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError('');
        if (!authForm.email) return;

        const emailLower = authForm.email.toLowerCase().trim();
        const exists = registeredUsers.some((u: any) => u.email.toLowerCase() === emailLower);
        if (exists) {
            setAuthStep('login');
        } else {
            setAuthStep('register');
        }
    };

    const handleLoginPasswordSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError('');
        const emailLower = authForm.email.toLowerCase().trim();
        const foundUser = registeredUsers.find((u: any) => u.email.toLowerCase() === emailLower);
        if (foundUser) {
            if (foundUser.password === authForm.password) {
                setUserVerification(foundUser);
                localStorage.setItem('currentUser', JSON.stringify(foundUser));
                setPaymentStep('upi');
                window.dispatchEvent(new Event('auth-change'));
            } else {
                setAuthError('Incorrect password. Please try again.');
            }
        } else {
            setAuthStep('email');
        }
    };

    const handleRegisterSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError('');

        if (!authForm.name || !authForm.email || !authForm.phone || !authForm.password) {
            setAuthError('All fields are required.');
            return;
        }

        if (authForm.password !== authForm.confirmPassword) {
            setAuthError('Passwords do not match.');
            return;
        }

        if (!agreeTerms) {
            setAuthError('You must agree to the Terms & Conditions.');
            return;
        }

        const emailLower = authForm.email.toLowerCase().trim();
        const exists = registeredUsers.some((u: any) => u.email.toLowerCase() === emailLower);
        if (exists) {
            setAuthError('User with this email already registered.');
            return;
        }

        const newUser = {
            name: authForm.name,
            email: authForm.email,
            phone: authForm.phone,
            password: authForm.password
        };

        const updatedUsers = [...registeredUsers, newUser];
        setRegisteredUsers(updatedUsers);
        localStorage.setItem('registeredUsers', JSON.stringify(updatedUsers));

        setUserVerification(newUser);
        localStorage.setItem('currentUser', JSON.stringify(newUser));
        setPaymentStep('upi');
        window.dispatchEvent(new Event('auth-change'));
    };

    const handleBackToEmail = () => {
        setAuthStep('email');
        setAuthForm(prev => ({ ...prev, password: '', confirmPassword: '' }));
        setAuthError('');
    };

    const handlePaymentSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (transactionId) {
            // Log booking transaction details locally
            const savedBookingsStr = localStorage.getItem('myBookings');
            const savedBookings = savedBookingsStr ? JSON.parse(savedBookingsStr) : [];
            const newBooking = {
                id: `AG-B-${Math.floor(100000 + Math.random() * 900000)}`,
                vehicleBrand: vehicle.Brand,
                vehicleName: vehicle.Name,
                vehicleImage: vehicle.Image[0],
                pickupDate: pickupDate.toISOString(),
                dropoffDate: dropoffDate.toISOString(),
                pickupLocation: selectedPickup,
                dropoffLocation: selectedDropoff,
                kms: selectedKMs,
                amount: getTotal().total,
                transactionId: transactionId,
                status: 'Pending Verification',
                dateBooked: new Date().toISOString(),
                payments: [
                    {
                        id: `PMT-${Math.floor(100000 + Math.random() * 900000)}`,
                        type: 'Booking Advance',
                        amount: getTotal().total,
                        transactionId: transactionId,
                        date: new Date().toISOString(),
                        status: 'Pending Verification'
                    }
                ]
            };
            savedBookings.unshift(newBooking);
            localStorage.setItem('myBookings', JSON.stringify(savedBookings));

            const whatsappLink = `https://wa.me/917208388380?text=${getMessage()}`;
            window.open(whatsappLink, '_blank', 'noopener,noreferrer');
            setShowAuthModal(false);
            setTransactionId('');
            setPaymentStep('verify');
        }
    };

    // SEO-friendly Dynamic Meta details
    const pageTitle = `Rent ${vehicle.Brand} ${vehicle.Name} in ${_.startCase(location)} | Cheap ${vehicle.isSelfDrive ? 'Self-Drive' : 'With Driver'} Rental`;
    const seoDescription = `Book a ${vehicle.isSelfDrive ? 'self-drive' : 'chauffeur-driven'} ${vehicle.Brand} ${vehicle.Name} rental in ${_.startCase(location)}. ${vehicle.Details.SeatingCapacity} seats, ${vehicle.Details.Fuel} fuel, and ₹${vehicle.Charges[0]} base price. Fully insured with local pick and drop options.`;

    // Dynamic SEO marketing text generator
    const getSeoDescriptionParagraph = () => {
        const activeHub = _.startCase(location);
        return `Rent the ${vehicle.Brand} ${vehicle.Name} for your next road trip across Maharashtra and the Konkan coast. Known for its reliable performance and smooth ${vehicle.Details.Transmission.toLowerCase()} transmission, this vehicle is exceptionally suited for local city drives and exploring tourist destinations around the ${activeHub} hub. Powered by an efficient ${vehicle.Details.Fuel.toLowerCase()} engine with a fuel mileage average of ${vehicle.Details['Avg/Ltr']} km/l, it offers the perfect balance of budget economy and spacious comfort for up to ${vehicle.Details.SeatingCapacity} travellers. Book this rental at the ${activeHub} hub with Aapli Gaadi today for a transparent, hassle-free booking experience backed by 24/7 roadside assistance.`;
    };

    return (
        <>
            <Helmet>
                <link rel="canonical" href={`https://www.aapligaadi.co.in/services/${serviceType}/${location}/${slug}`} />
            </Helmet>
            <SuperSEO
                title={pageTitle}
                description={seoDescription}
                lang="en"
                openGraph={{
                    ogTitle: pageTitle,
                    ogDescription: seoDescription,
                    ogUrl: `https://www.aapligaadi.co.in/services/${serviceType}/${location}/${slug}`,
                    ogType: "website",
                    ogSiteName: "Aapli Gaadi",
                    ogLocale: "en_US"
                }}
            />

            {/* Header / Breadcrumb Section */}
            <div className="hero-wrap ftco-degree-bg" style={{ backgroundImage: `url('/image/bg_3.jpg')`, height: '280px', backgroundPosition: 'center', backgroundSize: 'cover', position: 'relative' }}>
                <div className="overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#000', opacity: 0.4 }}></div>
                <div className="container" style={{ position: 'relative', height: '100%' }}>
                    <div className="row no-gutters slider-text justify-content-center align-items-center" style={{ height: '100%' }}>
                        <div className="col-md-9 text-center text-white" style={{ paddingTop: '80px' }}>
                            <p className="breadcrumbs mb-2" style={{ fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                <Link to="/" style={{ color: '#fff' }}>Home</Link> <span className="mx-2">&gt;</span>
                                <Link to={`/services/${serviceType}/${location}`} style={{ color: '#fff' }}>{_.startCase(location)} Hub</Link> <span className="mx-2">&gt;</span>
                                <span style={{ color: 'var(--primary-color)' }}>{vehicle.Brand} {vehicle.Name}</span>
                            </p>
                            <h1 className="mb-0 text-white font-weight-bold" style={{ fontSize: '32px' }}>{vehicle.Brand} {vehicle.Name}</h1>
                        </div>
                    </div>
                </div>
            </div>

            <section className="ftco-section bg-light" style={{ padding: '60px 0' }}>
                <div className="container">
                    <div className="row">
                        {/* Left Side: Images Cover Photo Slider, Specs, SEO Text */}
                        <div className="col-lg-7 col-md-12 mb-5">
                            {/* 100% Mobile-Friendly Image Slider & Gallery Component */}
                            <div className="card shadow-sm border-0 mb-4 overflow-hidden" style={{ borderRadius: '15px' }}>
                                <div className="position-relative w-100 d-flex align-items-center justify-content-center p-3" style={{ height: '350px', maxHeight: '55vh', backgroundColor: '#f8fafc' }}>
                                    <img
                                        src={`${process.env.PUBLIC_URL}/image/${isBikeRental ? 'bikes' : 'cars'}/${imageList[activeImgIdx]}`}
                                        alt={`${vehicle.Brand} ${vehicle.Name}`}
                                        className="img-fluid"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                            transition: 'all 0.3s ease-in-out'
                                        }}
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = `${process.env.PUBLIC_URL}/image/car-1.jpg`;
                                        }}
                                    />

                                    {/* Slider Left Arrow */}
                                    {imageList.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => setActiveImgIdx(prev => (prev === 0 ? imageList.length - 1 : prev - 1))}
                                            className="btn btn-dark d-flex align-items-center justify-content-center position-absolute"
                                            aria-label="Previous photo"
                                            style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', borderRadius: '50%', width: '38px', height: '38px', background: 'rgba(15, 23, 42, 0.75)', border: 'none', zIndex: 10, color: '#fff', fontSize: '18px' }}
                                        >
                                            ⟨
                                        </button>
                                    )}

                                    {/* Slider Right Arrow */}
                                    {imageList.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => setActiveImgIdx(prev => (prev === imageList.length - 1 ? 0 : prev + 1))}
                                            className="btn btn-dark d-flex align-items-center justify-content-center position-absolute"
                                            aria-label="Next photo"
                                            style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', borderRadius: '50%', width: '38px', height: '38px', background: 'rgba(15, 23, 42, 0.75)', border: 'none', zIndex: 10, color: '#fff', fontSize: '18px' }}
                                        >
                                            ⟩
                                        </button>
                                    )}

                                    {/* Service Badge Overlay */}
                                    <span className="badge badge-primary position-absolute" style={{ top: '12px', left: '12px', borderRadius: '20px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, zIndex: 10 }}>
                                        {vehicle.isSelfDrive ? 'Self Drive' : 'With Driver'}
                                    </span>
                                </div>

                                {/* Mobile-Friendly Thumbnail Previews & Dots */}
                                {imageList.length > 1 && (
                                    <div className="card-body bg-white border-top d-flex justify-content-center align-items-center flex-wrap py-2 px-3" style={{ gap: '8px' }}>
                                        {imageList.map((img, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => setActiveImgIdx(index)}
                                                className="btn p-0 border-0"
                                                style={{
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    width: '58px',
                                                    height: '40px',
                                                    border: activeImgIdx === index ? '2px solid var(--primary-color)' : '2px solid transparent',
                                                    boxShadow: activeImgIdx === index ? '0 0 0 2px rgba(255, 159, 28, 0.3)' : 'none',
                                                    opacity: activeImgIdx === index ? 1 : 0.65,
                                                    transition: 'all 0.2s ease',
                                                    cursor: 'pointer',
                                                    backgroundColor: '#f1f5f9'
                                                }}
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/image/${isBikeRental ? 'bikes' : 'cars'}/${img}`}
                                                    alt={`Thumbnail ${index + 1}`}
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).src = `${process.env.PUBLIC_URL}/image/car-1.jpg`;
                                                    }}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* SEO-friendly Details & specs Section */}
                            <div className="card shadow-sm border-0 p-4 mb-4" style={{ borderRadius: '15px' }}>
                                <h3 className="h4 font-weight-bold text-dark mb-3 border-bottom pb-2">Vehicle Overview</h3>
                                <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#475569', textAlign: 'justify' }}>
                                    {getSeoDescriptionParagraph()}
                                </p>
                            </div>

                            {/* Technical Specifications Grid */}
                            <div className="card shadow-sm border-0 p-4" style={{ borderRadius: '15px' }}>
                                <h3 className="h4 font-weight-bold text-dark mb-4 border-bottom pb-2">Technical Specifications</h3>
                                <div className="row">
                                    <div className="col-6 col-md-4 mb-4">
                                        <div className="p-3 bg-light rounded text-center h-100 d-flex flex-column justify-content-center">
                                            <span className="text-muted d-block small mb-1">Fuel Type</span>
                                            <strong className="text-dark">{vehicle.Details.Fuel}</strong>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4 mb-4">
                                        <div className="p-3 bg-light rounded text-center h-100 d-flex flex-column justify-content-center">
                                            <span className="text-muted d-block small mb-1">Seating Capacity</span>
                                            <strong className="text-dark">{vehicle.Details.SeatingCapacity} Seats</strong>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4 mb-4">
                                        <div className="p-3 bg-light rounded text-center h-100 d-flex flex-column justify-content-center">
                                            <span className="text-muted d-block small mb-1">Transmission</span>
                                            <strong className="text-dark">{vehicle.Details.Transmission}</strong>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4 mb-4">
                                        <div className="p-3 bg-light rounded text-center h-100 d-flex flex-column justify-content-center">
                                            <span className="text-muted d-block small mb-1">Luggage Capacity</span>
                                            <strong className="text-dark">{vehicle.Details.LuggageCapacity} Bags</strong>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4 mb-4">
                                        <div className="p-3 bg-light rounded text-center h-100 d-flex flex-column justify-content-center">
                                            <span className="text-muted d-block small mb-1">Mileage / Ltr</span>
                                            <strong className="text-dark">{vehicle.Details['Avg/Ltr']} KM</strong>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4 mb-4">
                                        <div className="p-3 bg-light rounded text-center h-100 d-flex flex-column justify-content-center">
                                            <span className="text-muted d-block small mb-1">Security Deposit</span>
                                            <strong className="text-dark">₹{vehicle.SecurityDeposit || 0}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side: Interactive Booking details widget & Pricing Breakup */}
                        <div className="col-lg-5 col-md-12">
                            <div className="card shadow-sm border-0 p-4 sticky-top" style={{ borderRadius: '15px', top: '100px', zIndex: 5 }}>
                                <h3 className="h4 font-weight-bold text-dark mb-4 border-bottom pb-2">Booking Calculator</h3>

                                {/* Trip Parameters Selection Menu */}
                                <div className="mb-4">
                                    <div className="form-group mb-3">
                                        <label className="label text-dark font-weight-bold small mb-1">Pick-up Location ({_.startCase(location)} Hub)</label>
                                        <select
                                            className="form-control"
                                            value={selectedPickup}
                                            onChange={(e) => {
                                                setSelectedPickup(e.target.value);
                                                if (!selectedDropoff) setSelectedDropoff(e.target.value);
                                            }}
                                            style={{ height: '46px', borderRadius: '8px' }}
                                        >
                                            {pickupPoints.map((loc) => (
                                                <option key={loc} value={loc}>{loc}</option>
                                            ))}
                                            {pickupPoints.length === 0 && (
                                                <option value="">No pick points configured</option>
                                            )}
                                        </select>
                                    </div>

                                    <div className="form-group mb-3">
                                        <label className="label text-dark font-weight-bold small mb-1">Drop-off Location ({_.startCase(location)} Hub)</label>
                                        <select
                                            className="form-control"
                                            value={selectedDropoff}
                                            onChange={(e) => setSelectedDropoff(e.target.value)}
                                            style={{ height: '46px', borderRadius: '8px' }}
                                        >
                                            {dropoffPoints.map((loc) => (
                                                <option key={loc} value={loc}>{loc}</option>
                                            ))}
                                            {dropoffPoints.length === 0 && (
                                                <option value="">No drop points configured</option>
                                            )}
                                        </select>
                                    </div>

                                    <div className="form-group mb-3">
                                        <label className="label text-dark font-weight-bold small mb-1">Pick-up Date & Time</label>
                                        <DatePicker
                                            selected={pickupDate}
                                            showTimeSelect
                                            dateFormat='dd MMM yyyy, h:mm a'
                                            onSelect={onPickupDateChange}
                                            onChange={onPickupDateChange}
                                            minDate={new Date()}
                                            customInput={<CustomDatePickerInput />}
                                            disabledKeyboardNavigation
                                        />
                                    </div>

                                    <div className="form-group mb-3">
                                        <label className="label text-dark font-weight-bold small mb-1">Drop-off Date & Time</label>
                                        <DatePicker
                                            selected={dropoffDate}
                                            showTimeSelect
                                            dateFormat='dd MMM yyyy, h:mm a'
                                            onSelect={(date) => date && setDropoffDate(date)}
                                            onChange={(date) => date && setDropoffDate(date)}
                                            minDate={getMinDropoffDate()}
                                            customInput={<CustomDatePickerInput />}
                                            disabledKeyboardNavigation
                                        />
                                    </div>

                                    {vehicle.KMIncluded && (
                                        <div className="form-group mb-3">
                                            <label className="label text-dark font-weight-bold small mb-1">Select KM Plan Package</label>
                                            {rangeInfo.isRestricted ? (
                                                <div>
                                                    <select
                                                        className="form-control text-muted"
                                                        disabled
                                                        value=""
                                                        style={{ height: '46px', borderRadius: '8px' }}
                                                    >
                                                        <option value="">Restricted</option>
                                                    </select>
                                                    <div className="text-danger small font-weight-bold mt-1">
                                                        ⚠️ {rangeInfo.restrictionMessage}
                                                    </div>
                                                </div>
                                            ) : (
                                                <select
                                                    className="form-control font-weight-bold"
                                                    value={selectedKMs || (rangeInfo.allowedKMs[0] || '')}
                                                    onChange={(e) => setSelectedKMs(e.target.value)}
                                                    style={{ height: '46px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                    disabled={rangeInfo.allowedKMs.length <= 1}
                                                >
                                                    {rangeInfo.allowedKMs.map((km) => (
                                                        <option key={km} value={km}>{km} KMs / Day</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Pricing Breakdown Panel */}
                                <div className="p-3 bg-light rounded mb-4" style={{ border: '1px dashed #cbd5e1' }}>
                                    <h4 className="h6 font-weight-bold text-dark mb-3 border-bottom pb-2">Price Breakdown</h4>
                                    <ul className="list-unstyled mb-0" style={{ fontSize: '13.5px', color: '#475569' }}>
                                        <li className="d-flex justify-content-between mb-2">
                                            <span>Rental Period:</span>
                                            <strong>{getDays()} Day(s)</strong>
                                        </li>
                                        <li className="d-flex justify-content-between mb-2">
                                            <span>Included KMs:</span>
                                            <strong>{getVehicleCharges().kmPerDay === 'Unlimited' ? 'Unlimited' : parseInt(getVehicleCharges().kmPerDay) * getDays()} KMs</strong>
                                        </li>
                                        <li className="d-flex justify-content-between mb-2">
                                            <span>Base Fleet Rate:</span>
                                            <strong>₹{calculateCharges()}</strong>
                                        </li>
                                        {vehicle.isSelfDrive && (
                                            <>
                                                <li className="d-flex justify-content-between mb-2">
                                                    <span>Convenience Pickup Fee:</span>
                                                    <strong>₹{getPickupCharges()}</strong>
                                                </li>
                                                <li className="d-flex justify-content-between mb-2">
                                                    <span>Convenience Dropoff Fee:</span>
                                                    <strong>₹{getDropoffCharges()}</strong>
                                                </li>
                                            </>
                                        )}
                                        {isOvernightChargeApplicable() && (
                                            <li className="d-flex justify-content-between mb-2">
                                                <span>Overnight Charge:</span>
                                                <strong>₹{getOvernightCharges()}</strong>
                                            </li>
                                        )}
                                        <li className="d-flex justify-content-between mb-2">
                                            <span>Platform Handling Fees:</span>
                                            <strong>₹{selectedPickup && selectedDropoff ? ((charges as any)[selectedPickup]?.['Platform Charges'] || 0) + ((charges as any)[selectedDropoff]?.['Platform Charges'] || 0) : 0}</strong>
                                        </li>

                                        {vehicle.isSelfDrive && getTotal().actualCharges - getTotal().total > 0 && (
                                            <li className="d-flex justify-content-between mb-2 text-success font-weight-bold">
                                                <span>Discount Applied:</span>
                                                <span>-₹{getTotal().actualCharges - getTotal().total}</span>
                                            </li>
                                        )}

                                        <li className="d-flex justify-content-between pt-2 border-top mt-2 text-dark font-weight-bold" style={{ fontSize: '16px' }}>
                                            <span>Total Charges:</span>
                                            <span style={{ color: 'var(--primary-color)' }}>₹{getTotal().total}/-</span>
                                        </li>
                                    </ul>
                                </div>

                                {/* Call to action button */}
                                {rangeInfo.isRestricted ? (
                                    <button
                                        type="button"
                                        disabled
                                        className="btn btn-secondary btn-block py-3 font-weight-bold"
                                        style={{ borderRadius: '30px', fontSize: '16px', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.65, cursor: 'not-allowed' }}
                                    >
                                        1-Day Booking Restricted
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleBookingClick}
                                        className="btn btn-primary btn-block py-3 font-weight-bold"
                                        style={{ borderRadius: '30px', fontSize: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}
                                    >
                                        Book Now via WhatsApp
                                    </button>
                                )}

                                {vehicle.isSelfDrive && (
                                    <p className="text-center text-muted small mt-3 mb-0">
                                        * A Refundable Security Deposit of ₹{vehicle.SecurityDeposit} is payable at pick-up.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Login / Registration and UPI Payment Modal */}
            {showAuthModal && (
                <div className="modal-backdrop-blur" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(15, 23, 42, 0.65)',
                    backdropFilter: 'blur(5px)',
                    zIndex: 1050,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div className="card shadow-lg border-0" style={{
                        maxWidth: '480px',
                        width: '100%',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        backgroundColor: '#ffffff'
                    }}>
                        {/* Modal Header */}
                        <div className="card-header bg-white border-bottom-0 d-flex justify-content-between align-items-center pt-4 px-4 pb-2">
                            <h3 className="h5 font-weight-bold text-dark mb-0">
                                {paymentStep === 'verify' ? (
                                    authStep === 'email' ? 'Register / Login' :
                                        authStep === 'login' ? 'Log In' : 'Create Account'
                                ) : 'Complete Booking'}
                            </h3>
                            <button
                                type="button"
                                className="btn btn-sm btn-light p-0"
                                onClick={() => {
                                    setShowAuthModal(false);
                                    setPaymentStep('verify');
                                    setAuthStep('email');
                                    setAuthError('');
                                    setTransactionId('');
                                }}
                                style={{ borderRadius: '50%', width: '30px', height: '30px', border: '1px solid #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="card-body p-4">
                            {paymentStep === 'verify' ? (
                                <>
                                    {authError && (
                                        <div className="alert alert-danger py-2 px-3 small rounded mb-3 text-center" style={{ fontSize: '13px' }}>
                                            {authError}
                                        </div>
                                    )}

                                    {authStep === 'email' && (
                                        <form onSubmit={handleEmailContinue}>
                                            <p className="text-muted small mb-4">Enter your email address to continue with your booking.</p>
                                            <div className="form-group mb-4">
                                                <label className="text-dark font-weight-bold small mb-1">Email Address</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    required
                                                    value={authForm.email}
                                                    onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                                                    placeholder="name@example.com"
                                                    style={{ height: '42px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-block py-2 font-weight-bold"
                                                style={{ borderRadius: '30px', fontSize: '15px' }}
                                            >
                                                Continue
                                            </button>
                                        </form>
                                    )}

                                    {authStep === 'login' && (
                                        <form onSubmit={handleLoginPasswordSubmit}>
                                            <div className="form-group mb-3">
                                                <label className="text-muted small mb-1">Logging in as:</label>
                                                <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light mb-3" style={{ border: '1px solid #e2e8f0' }}>
                                                    <span className="text-dark small font-weight-bold" style={{ wordBreak: 'break-all' }}>{authForm.email}</span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 small text-primary"
                                                        onClick={handleBackToEmail}
                                                        style={{ fontSize: '12px' }}
                                                    >
                                                        Change
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="form-group mb-4">
                                                <label className="text-dark font-weight-bold small mb-1">Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    required
                                                    value={authForm.password}
                                                    onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                                                    placeholder="Enter your password"
                                                    style={{ height: '42px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-block py-2 font-weight-bold"
                                                style={{ borderRadius: '30px', fontSize: '15px' }}
                                            >
                                                Log In
                                            </button>
                                        </form>
                                    )}

                                    {authStep === 'register' && (
                                        <form onSubmit={handleRegisterSubmit}>
                                            <div className="form-group mb-3">
                                                <label className="text-muted small mb-1">Registering email:</label>
                                                <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light mb-3" style={{ border: '1px solid #e2e8f0' }}>
                                                    <span className="text-dark small font-weight-bold" style={{ wordBreak: 'break-all' }}>{authForm.email}</span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 small text-primary"
                                                        onClick={handleBackToEmail}
                                                        style={{ fontSize: '12px' }}
                                                    >
                                                        Change
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="form-group mb-3">
                                                <label className="text-dark font-weight-bold small mb-1">Full Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    required
                                                    value={authForm.name}
                                                    onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                                                    placeholder="Enter your full name"
                                                    style={{ height: '42px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                />
                                            </div>
                                            <div className="form-group mb-3">
                                                <label className="text-dark font-weight-bold small mb-1">Phone Number</label>
                                                <input
                                                    type="tel"
                                                    className="form-control"
                                                    required
                                                    pattern="[0-9]{10}"
                                                    title="Please enter a valid 10-digit mobile number"
                                                    value={authForm.phone}
                                                    onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })}
                                                    placeholder="10-digit mobile number"
                                                    style={{ height: '42px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                />
                                            </div>
                                            <div className="form-group mb-3">
                                                <label className="text-dark font-weight-bold small mb-1">Create Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    required
                                                    value={authForm.password}
                                                    onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                                                    placeholder="Choose a password"
                                                    style={{ height: '42px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                />
                                            </div>
                                            <div className="form-group mb-3">
                                                <label className="text-dark font-weight-bold small mb-1">Confirm Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    required
                                                    value={authForm.confirmPassword}
                                                    onChange={(e) => setAuthForm({ ...authForm, confirmPassword: e.target.value })}
                                                    placeholder="Confirm your password"
                                                    style={{ height: '42px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                />
                                            </div>
                                            <div className="form-group mb-4 form-check d-flex align-items-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0 mr-2"
                                                    id="agreeTermsCheck"
                                                    required
                                                    checked={agreeTerms}
                                                    onChange={(e) => setAgreeTerms(e.target.checked)}
                                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                />
                                                <label className="form-check-label text-dark small mb-0" htmlFor="agreeTermsCheck" style={{ cursor: 'pointer', userSelect: 'none' }}>
                                                    I agree to the{' '}
                                                    <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="font-weight-bold text-primary" style={{ textDecoration: 'underline' }}>
                                                        Terms & Conditions
                                                    </a>
                                                </label>
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-block py-2 font-weight-bold"
                                                style={{ borderRadius: '30px', fontSize: '15px' }}
                                            >
                                                Register & Log In
                                            </button>
                                        </form>
                                    )}
                                </>
                            ) : (
                                <form onSubmit={handlePaymentSubmit} className="text-center">
                                    {/* Mobile Direct Intent Button (Bypasses NPCI Gallery Screenshot Limits) */}
                                    {isMobile && (
                                        <div className="mb-4">
                                            <a
                                                href={getUpiUri()}
                                                className="btn btn-success btn-block py-3 font-weight-bold shadow-sm"
                                                style={{ borderRadius: '30px', fontSize: '15px', backgroundColor: '#01d28e', borderColor: '#01d28e' }}
                                            >
                                                🚀 Open UPI Payment App (₹{getTotal().total.toLocaleString('en-IN')})
                                            </a>
                                            <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                                                Directly launches your UPI payment app without gallery screenshot errors!
                                            </small>
                                        </div>
                                    )}

                                    {/* NPCI Gallery Screenshot Warning Notice */}
                                    <div className="alert alert-warning text-left p-2 px-3 mb-3 rounded" style={{ fontSize: '12px', backgroundColor: '#fffbe6', borderColor: '#ffe58f', color: '#7c5e10' }}>
                                        <strong>⚠️ Note:</strong> UPI apps cap gallery QR screenshot uploads to <strong>₹2,000 max</strong>. On mobile, use <strong>"Open UPI Payment App"</strong> above or scan live from another phone.
                                    </div>

                                    {/* Scanable QR Code */}
                                    <div className="d-inline-block p-3 rounded mb-3 bg-white" style={{ border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                        <img
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(getUpiUri())}`}
                                            alt="UPI QR Code"
                                            style={{ width: '180px', height: '180px', display: 'block' }}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <h4 className="h6 font-weight-bold text-dark mb-1">Payable Amount: ₹{getTotal().total.toLocaleString('en-IN')}/-</h4>
                                        <span className="badge badge-light text-muted p-2" style={{ fontSize: '12px', border: '1px solid #cbd5e1' }}>
                                            UPI ID: crewtechsolutions@upi
                                        </span>
                                    </div>

                                    {/* Transaction ID Verification field */}
                                    <div className="form-group mb-3 text-left">
                                        <label className="text-dark font-weight-bold small mb-1">Enter UPI Transaction ID / Ref No.</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            required
                                            pattern="[0-9]{12}"
                                            title="Please enter the exact 12-digit transaction or reference number from your payment app"
                                            value={transactionId}
                                            onChange={(e) => setTransactionId(e.target.value)}
                                            placeholder="12-digit transaction number"
                                            style={{ height: '42px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                        />
                                        <small className="text-muted d-block mt-1" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                            Required to confirm payment. Find the 12-digit transaction ID in your UPI app summary.
                                        </small>
                                    </div>

                                    <div className="text-muted small mb-3 font-italic" style={{ fontSize: '11px' }}>
                                        Registered as: {userVerification?.name} ({userVerification?.phone}) |{' '}
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 small font-weight-bold text-primary"
                                            onClick={() => {
                                                setPaymentStep('verify');
                                                setAuthStep('email');
                                                setAuthError('');
                                            }}
                                            style={{ fontSize: '11px', textDecoration: 'underline' }}
                                        >
                                            Change details
                                        </button>
                                    </div>

                                    {/* Booking Terms Agreement */}
                                    <div className="form-group mb-4 form-check d-flex align-items-center justify-content-center">
                                        <input
                                            type="checkbox"
                                            className="form-check-input mt-0 mr-2"
                                            id="agreeBookingTermsCheck"
                                            required
                                            checked={agreeBookingTerms}
                                            onChange={(e) => setAgreeBookingTerms(e.target.checked)}
                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                        <label className="form-check-label text-dark small mb-0" htmlFor="agreeBookingTermsCheck" style={{ cursor: 'pointer', userSelect: 'none' }}>
                                            I agree to the{' '}
                                            <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="font-weight-bold text-primary" style={{ textDecoration: 'underline' }}>
                                                Terms & Conditions
                                            </a>
                                        </label>
                                    </div>

                                    <hr className="my-3" />

                                    {/* Final WhatsApp Booking Confirmation Trigger */}
                                    <button
                                        type="submit"
                                        className="btn btn-success btn-block py-3 font-weight-bold d-flex align-items-center justify-content-center"
                                        style={{
                                            borderRadius: '30px',
                                            fontSize: '14px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            cursor: (transactionId.length !== 12 || !/^[0-9]{12}$/.test(transactionId) || !agreeBookingTerms) ? 'not-allowed' : 'pointer'
                                        }}
                                        disabled={transactionId.length !== 12 || !/^[0-9]{12}$/.test(transactionId) || !agreeBookingTerms}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="mr-2" viewBox="0 0 16 16">
                                            <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.907h.004c4.368 0 7.926-3.558 7.93-7.93a7.897 7.897 0 0 0-2.327-5.615zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.69-4.98c-.202-.101-1.194-.59-1.383-.658-.19-.068-.327-.101-.465.101-.138.203-.534.658-.654.793-.12.136-.24.153-.442.052-1.742-.87-2.842-1.792-3.411-2.768-.15-.258-.014-.397.116-.527.117-.117.258-.3.388-.45.13-.15.172-.25.258-.418.087-.168.044-.313-.022-.449-.067-.136-.59-1.42-.81-1.948-.214-.515-.43-.444-.59-.452-.15-.008-.328-.01-.497-.01a.985.985 0 0 0-.703.308c-.242.259-.922.9-.922 2.2 0 1.3 1.0 2.5 1.1 2.7.1.2 1.9 3.0 4.7 4.1.6.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.7-.7 1.9-1.3.2-.6.2-1.2.1-1.3-.1-.1-.3-.2-.5-.3z" />
                                        </svg>
                                        Confirm Booking via WhatsApp
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default VehicleDetail;
